import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { settingsApi, servicesApi } from '../lib/api';

export function Header() {
  const location = useLocation();
  const [isSolutionsHovered, setIsSolutionsHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const { data: settings } = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => settingsApi.getPublic().then((res) => res.data),
    staleTime: 0, // Always refetch
    refetchOnMount: true,
  });

  const { data: services } = useQuery({
    queryKey: ['services', true],
    queryFn: () => servicesApi.getAll(true).then((res) => res.data),
  });

  const isActive = (path: string) => location.pathname === path;

  const renderLogo = () => {
    if (settings?.company?.logo?.type === 'image' && settings?.company?.logo?.url) {
      return (
        <Link to="/" className="flex items-center">
          <img
            src={settings.company.logo.url}
            alt={settings.company.name || 'Logo'}
            className="w-[190px] object-contain"
            onError={(e) => {
              // Fallback to text if image fails
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </Link>
      );
    }
    return (
      <Link to="/" className="text-2xl font-bold text-primary-600">
        {settings?.company?.name || import.meta.env.VITE_APP_NAME || 'KUMBİLGİ'}
      </Link>
    );
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-16">
          {renderLogo()}

          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`${
                isActive('/') ? 'text-primary-600' : 'text-gray-700'
              } hover:text-primary-600 transition-colors`}
            >
              Ana Sayfa
            </Link>
            <Link
              to="/hakkimizda"
              className={`${
                isActive('/hakkimizda') ? 'text-primary-600' : 'text-gray-700'
              } hover:text-primary-600 transition-colors`}
            >
              Hakkımızda
            </Link>
            <div
              className="relative"
              onMouseEnter={() => setIsSolutionsHovered(true)}
              onMouseLeave={() => setIsSolutionsHovered(false)}
            >
              <Link
                to="/cozumler"
                className={`${
                  isActive('/cozumler') ? 'text-primary-600' : 'text-gray-700'
                } hover:text-primary-600 transition-colors`}
              >
                Çözümler
              </Link>
              {isSolutionsHovered && services && services.length > 0 && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-64 w-auto">
                    {services.map((service: any) => (
                      <Link
                        key={service.id}
                        to={`/cozumler/${service.slug}`}
                        className="block px-4 py-1 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link
              to="/blog"
              className={`${
                isActive('/blog') ? 'text-primary-600' : 'text-gray-700'
              } hover:text-primary-600 transition-colors`}
            >
              Blog
            </Link>
            <Link
              to="/iletisim"
              className={`${
                isActive('/iletisim') ? 'text-primary-600' : 'text-gray-700'
              } hover:text-primary-600 transition-colors`}
            >
              İletişim
            </Link>
            <Link
              to="/iletisim"
              className="btn btn-primary"
            >
              Teklif Al
            </Link>
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menüyü aç/kapat"
          >
            {isMobileMenuOpen ? (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className={`${
                  isActive('/') ? 'text-primary-600' : 'text-gray-700'
                } hover:text-primary-600 transition-colors px-4`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Ana Sayfa
              </Link>
              <Link
                to="/hakkimizda"
                className={`${
                  isActive('/hakkimizda') ? 'text-primary-600' : 'text-gray-700'
                } hover:text-primary-600 transition-colors px-4`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Hakkımızda
              </Link>
              <div className="px-4">
                <Link
                  to="/cozumler"
                  className={`${
                    isActive('/cozumler') ? 'text-primary-600' : 'text-gray-700'
                  } hover:text-primary-600 transition-colors block mb-2`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Çözümler
                </Link>
                {services && services.length > 0 && (
                  <div className="pl-4 space-y-2">
                    {services.map((service: any) => (
                      <Link
                        key={service.id}
                        to={`/cozumler/${service.slug}`}
                        className="block text-sm text-gray-600 hover:text-primary-600 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/blog"
                className={`${
                  isActive('/blog') ? 'text-primary-600' : 'text-gray-700'
                } hover:text-primary-600 transition-colors px-4`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                to="/iletisim"
                className={`${
                  isActive('/iletisim') ? 'text-primary-600' : 'text-gray-700'
                } hover:text-primary-600 transition-colors px-4`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                İletişim
              </Link>
              <Link
                to="/iletisim"
                className="btn btn-primary mx-4"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Teklif Al
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

